import {
  FC,
  HTMLAttributes,
  ReactElement,
  cloneElement,
  useEffect,
  useState
} from 'react';

import { makeClass } from '../../../utils';

export interface CSSTransitionProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Show the component
   */
  in?: boolean;
  /**
   * Trigger enter animation on mount
   */
  appear?: boolean;
  /**
   * Trigger the enter animation
   */
  enter?: boolean;
  /**
   * Trigger the exit animation
   */
  exit?: boolean;
  /**
   * Duration of the transition
   */
  timeout?: number;
  /**
   * Triggered when the enter transition is completed.
   */
  onEntered?: (entered: boolean) => void;
  /**
   * Triggered when the exit transition is completed.
   */
  onExited?: (entered: boolean) => void;
  /**
   * Unmount the component when the `exit` state is reached
   */
  unmountOnExit?: boolean;
}

enum STATUS {
  ENTERED,
  ENTERING,
  EXITING,
  EXITED
}

const CSSTransition: FC<CSSTransitionProps> = (props: CSSTransitionProps) => {
  const {
    in: _in,
    appear,
    children,
    className,
    timeout = 150,
    onEntered,
    onExited,
    unmountOnExit,
    enter,
    exit,
    ...rest
  } = props;

  const rootPrefixedClassNames = (root: string, ...classList: string[]) =>
    makeClass([root, ...classList.map((classN) => `${root}-${classN}`)]);

  const [classes, setClasses] = useState(
    rootPrefixedClassNames(className, 'initial')
  );
  const [status, setStatus] = useState(_in ? STATUS.ENTERING : STATUS.EXITED);
  const [_appear, setAppear] = useState(appear);

  useEffect(() => {
    if (_in && (appear || enter)) {
      setStatus(STATUS.ENTERING);
      setTimeout(() => setStatus(STATUS.ENTERED), timeout);
      setAppear(false);
    } else {
      if (!_appear || exit) {
        setAppear(appear);
        setStatus(STATUS.EXITING);
        setTimeout(() => setStatus(STATUS.EXITED), timeout);
      }
    }
    return () => {
      setStatus(STATUS.EXITED);
    };
  }, [_in]);

  useEffect(() => {
    switch (status) {
      case STATUS.ENTERING:
        setClasses(
          makeClass([className, `${className}-enter`, 'enter-active'])
        );
        break;
      case STATUS.ENTERED:
        onEntered?.(true);
        setClasses(
          rootPrefixedClassNames(className, 'initial', 'enter', 'enter-done')
        );
        break;
      case STATUS.EXITING:
        setClasses(rootPrefixedClassNames(className, `exit`, 'exit-active'));
        break;
      case STATUS.EXITED:
        onExited?.(true);
        setClasses(
          rootPrefixedClassNames(className, 'initial', 'exit', 'exit-done')
        );
        break;
    }
  }, [status]);

  const shouldUnMount = unmountOnExit && status === STATUS.EXITED;

  return shouldUnMount
    ? null
    : cloneElement(children as ReactElement, {
        className: makeClass([
          classes,
          (children as ReactElement).props.className
        ]),
        ...rest
      });
};

export default CSSTransition;
